/**
 * Extract subdomain from hostname
 * @param hostname - The hostname (e.g., "mysubdomain.localhost" or "mysubdomain.site.com")
 * @param baseDomain - The base domain (e.g., "localhost" or "site.com")
 * @returns The subdomain or null if no subdomain found
 */
export function extractSubdomain(hostname: string, baseDomain: string): string | null {
  if (!hostname || !baseDomain) return null;
  
  // Remove port if present
  const hostWithoutPort = hostname.split(':')[0];
  
  // Check if hostname contains base domain
  if (!hostWithoutPort.includes(baseDomain)) return null;
  
  const parts = hostWithoutPort.split('.');
  const baseParts = baseDomain.split('.');
  
  // If host has more parts than base domain, first part is subdomain
  if (parts.length > baseParts.length) {
    const subdomain = parts[0].toLowerCase();
    // Exclude 'www' as it's not a real subdomain
    if (subdomain !== 'www' && subdomain !== 'localhost') {
      return subdomain;
    }
  }
  
  return null;
}

/**
 * Get the base domain from environment or current hostname
 */
export function getBaseDomain(): string {
  // In production, this should be set via environment variable
  const envBaseDomain = import.meta.env.VITE_BASE_DOMAIN;
  if (envBaseDomain) {
    return envBaseDomain;
  }
  
  // Fallback: try to extract from current hostname
  const hostname = window.location.hostname;
  
  // For localhost (with or without subdomain), return 'localhost'
  if (hostname === 'localhost' || hostname.endsWith('.localhost') || hostname.startsWith('127.0.0.1')) {
    return 'localhost';
  }
  
  // For other domains, try to extract base domain
  // This is a simple heuristic - in production, use env var
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return parts.slice(-2).join('.'); // Last two parts (e.g., "site.com")
  }
  
  return hostname;
}

/**
 * Check if current domain is a subdomain
 */
export function isSubdomain(): boolean {
  const hostname = window.location.hostname;
  const baseDomain = getBaseDomain();
  const subdomain = extractSubdomain(hostname, baseDomain);
  return subdomain !== null;
}

/**
 * Get current subdomain if present
 */
export function getCurrentSubdomain(): string | null {
  const hostname = window.location.hostname;
  const baseDomain = getBaseDomain();
  return extractSubdomain(hostname, baseDomain);
}

/**
 * Get the full subdomain URL
 */
export function getSubdomainUrl(subdomain: string, baseDomain?: string): string {
  const domain = baseDomain || getBaseDomain();
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${protocol}//${subdomain}.${domain}${port}`;
}

